"use client";

import { useRouter } from "next/navigation";
import { studioViewPath } from "@/lib/studio/studio-routes";
import {
  QUALITY_LABEL,
  intervalDegreeLabel,
  type ChordEntry,
} from "@/lib/music/chord-catalog";
import { useChordLabel } from "@/hooks/use-notation";
import {
  BORDER,
  FONT_MONO,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
  VIOLET_SOFT,
} from "./palette";

type Props = {
  entry: ChordEntry;
  variants: ChordEntry[];
  /** Called when the user picks a variant. If omitted, falls back to a
   *  router push to /studio/chords?practice=<id> (legacy URL-driven flow). */
  onSelect?: (entry: ChordEntry) => void;
};

export function ChordPracticeGridVariants({ entry, variants, onSelect }: Props) {
  const router = useRouter();
  const chordLabel = useChordLabel();

  const pickVariant = (v: ChordEntry) => {
    if (onSelect) {
      onSelect(v);

      return;
    }

    router.push(
      `${studioViewPath("chords")}?practice=${encodeURIComponent(v.id)}`,
    );
  };

  return (
    <section
      className="relative z-10 flex shrink-0 items-center"
      style={{
        padding: "12px 24px",
        gap: 12,
        borderTop: `1px solid ${BORDER}`,
        background: "rgba(10, 10, 14, 0.6)",
      }}
    >
      <span
        style={{
          fontSize: 9,
          fontFamily: FONT_MONO,
          color: TEXT_MUTE,
          letterSpacing: "0.18em",
          fontWeight: 600,
          flexShrink: 0,
          textTransform: "uppercase",
        }}
      >
        Variantes
      </span>
      <div
        className="chord-practice-variants"
        style={{
          display: "flex",
          gap: 6,
          flex: 1,
          overflowX: "auto",
          overflowY: "hidden",
        }}
      >
        {variants.map((v) => {
          const isActive = v.id === entry.id;
          const formula = v.intervals.map(intervalDegreeLabel).join(" · ");

          return (
            <button
              key={v.id}
              type="button"
              onClick={() => pickVariant(v)}
              disabled={isActive}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                background: isActive ? VIOLET_SOFT : "rgba(255,255,255,0.02)",
                border: `1px solid ${isActive ? VIOLET_GLOW : BORDER}`,
                cursor: isActive ? "default" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
                fontFamily: "inherit",
                transition: "background-color 150ms, border-color 150ms",
              }}
            >
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 12,
                  fontWeight: 500,
                  color: isActive ? TEXT : TEXT_DIM,
                }}
              >
                {chordLabel(v.symbol)}
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: isActive ? VIOLET_BRIGHT : TEXT_MUTE,
                }}
              >
                {QUALITY_LABEL[v.quality]}
              </span>
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 9,
                  color: TEXT_MUTE,
                  letterSpacing: "0.05em",
                }}
              >
                {formula}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
